import { Box, useColorModeValue, type BoxProps } from '@chakra-ui/react';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

// Trimmed port of Kaleidoscope's MobileFriendlyTableWrapper — just the wrapper
// the Bizy table chrome needs (the StickyTd / SkeletonTableRows helpers and
// their `rem` dependency are not used here).

const OverflowContext = createContext<boolean | undefined>(undefined);

export const useOverflowContext = () => {
	const context = useContext(OverflowContext);
	if (context === undefined) {
		throw new Error('useOverflowContext must be used within a MobileFriendlyTableWrapper');
	}
	return context;
};

interface MobileFriendlyTableWrapperProps extends BoxProps {
	children: ReactNode;
}

export const MobileFriendlyTableWrapper = ({ children, ...styleProps }: MobileFriendlyTableWrapperProps) => {
	const [hasOverflow, setHasOverflow] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	const leftFadeRef = useRef<HTMLDivElement>(null);
	const rightFadeRef = useRef<HTMLDivElement>(null);
	const hasOverflowRef = useRef(false);
	const rightFadeGradient = useColorModeValue(
		'linear-gradient(90deg, rgba(221, 221, 221, 0) 0%, rgba(221, 221, 221, 0.5) 100%)',
		'linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)'
	);
	const leftFadeGradient = useColorModeValue(
		'linear-gradient(270deg, rgba(221, 221, 221, 0) 0%, rgba(221, 221, 221, 0.5) 100%)',
		'linear-gradient(270deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 100%)'
	);

	const updateScrollState = useCallback(() => {
		const el = containerRef.current;
		const leftFade = leftFadeRef.current;
		const rightFade = rightFadeRef.current;
		if (!el || !leftFade || !rightFade) return;
		const { scrollLeft, scrollWidth, clientWidth } = el;
		const overflow = scrollWidth > clientWidth;
		const atRightEdge = scrollLeft + clientWidth >= scrollWidth - 1;
		leftFade.style.display = overflow && scrollLeft > 0 ? 'block' : 'none';
		rightFade.style.display = overflow && !atRightEdge ? 'block' : 'none';
		if (overflow !== hasOverflowRef.current) {
			hasOverflowRef.current = overflow;
			setHasOverflow(overflow);
		}
	}, []);

	useEffect(() => {
		const timeoutId = setTimeout(updateScrollState, 100);
		window.addEventListener('resize', updateScrollState);
		const observer = new MutationObserver(() => {
			setTimeout(updateScrollState, 10);
		});
		if (containerRef.current) {
			observer.observe(containerRef.current, { childList: true, subtree: true });
		}
		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('resize', updateScrollState);
			observer.disconnect();
		};
	}, [updateScrollState]);

	return (
		<OverflowContext.Provider value={hasOverflow}>
			<Box position="relative" {...styleProps}>
				<Box
					ref={leftFadeRef}
					position="absolute"
					top={0}
					left={0}
					height="100%"
					width="20px"
					background={leftFadeGradient}
					pointerEvents="none"
					zIndex={0}
					display="none"
				/>
				<Box
					ref={rightFadeRef}
					position="absolute"
					top={0}
					right={0}
					height="100%"
					width="20px"
					background={rightFadeGradient}
					pointerEvents="none"
					zIndex={3}
					display="none"
				/>
				<Box ref={containerRef} overflowX="auto" onScroll={updateScrollState}>
					{children}
				</Box>
			</Box>
		</OverflowContext.Provider>
	);
};
